/**
 * Capture (Console V7) helpers: the design's own style strings, copied value for value from
 * capture.js (the V7 package's Capture logic), its money format and its sparkline maths.
 * Every figure the page shows comes from the API; these only shape it.
 */
import { honestyOf, type ApiClient, type ApiError, type HonestyFlags } from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { useQuery, type QueryState } from '../../useQuery';

export const pre = (e?: { preventDefault?: () => void }) => e?.preventDefault?.();

/** Load one route with the session's client. */
export function useCaptureApi<T>(load: (client: ApiClient, signal: AbortSignal) => Promise<T>, deps: unknown[] = [], enabled = true): QueryState<T> {
  const { client, session } = useSession();
  return useQuery<T>((signal) => load(client, signal), [client, session?.token, ...deps], { enabled });
}

/** A route that needs one building answers 400 when none is chosen. */
export const needsBuilding = (e: ApiError | null | undefined) => !!e && (e.status === 400 || e.kind === 'validation');

// ── Honesty labels (design badge(), capture.js L45) ─────────────────────────
export type Honesty = 'MEASURED' | 'MODELED' | 'SAMPLE' | 'ESTIMATED' | 'WHAT-IF' | 'PUBLISHED' | 'est.';
/** A customer's quiet "est." (drawn after the number, never as a chip). */
export const isEst = (b: Honesty | null | undefined): boolean => b === 'est.';
const BADGE_C: Record<string, [string, string]> = {
  MEASURED: ['rgba(31,107,82,.1)', '#1F6B52'],
  MODELED: ['rgba(107,74,18,.1)', '#6B4A12'],
  SAMPLE: ['rgba(34,39,46,.08)', '#54606C'],
  ESTIMATED: ['rgba(47,92,151,.1)', '#2F5C97'],
  'WHAT-IF': ['rgba(107,74,18,.1)', '#6B4A12'],
  PUBLISHED: ['rgba(31,107,82,.1)', '#1F6B52'],
};
/** The small mono label beside a figure (design gen badge). */
export const badge = (t: string) => {
  const c = BADGE_C[t] ?? BADGE_C.SAMPLE;
  return "font-family:'IBM Plex Mono',monospace;font-size:14px;font-weight:600;padding:1px 5px;border-radius:3px;white-space:nowrap;align-self:flex-start;background:" + c[0] + ';color:' + c[1] + ';';
};
/** The same label as Snapshot draws it (design rv3 badge, no align-self). */
export const badgeInline = (t: string) => {
  const c = BADGE_C[t] ?? BADGE_C.SAMPLE;
  return "font-family:'IBM Plex Mono',monospace;font-size:14px;font-weight:600;padding:1px 5px;border-radius:3px;white-space:nowrap;background:" + c[0] + ';color:' + c[1] + ';';
};

/** The API's honesty word for a figure (MEASURED, MODELED, SAMPLE, ESTIMATED), or null. */
export const honest = (...flags: (HonestyFlags | null | undefined)[]): Honesty | null => honestyOf(...flags);

// ── Tabs, chips, buttons (capture.js) ───────────────────────────────────────
/** The seven page tabs (design tab(), L12). */
export const pvTab = (on: boolean) =>
  'height:30px;padding:0 11px;display:inline-flex;align-items:center;border-radius:7px;font-size:16px;text-decoration:none;white-space:nowrap;' +
  (on ? 'background:rgba(47,92,151,.12);color:#22272E;font-weight:700;' : 'color:#3C454F;font-weight:600;');
/** This building / Portfolio / Network (design scopeChips, L22). */
export const scopeChip = (on: boolean) =>
  'height:26px;padding:0 10px;display:inline-flex;align-items:center;border-radius:6px;font-size:14px;font-weight:700;white-space:nowrap;text-decoration:none;' +
  (on ? 'background:#22272E;color:#F6F3EC;' : 'color:#3C454F;');
/** An Ask Atlas question (design askChips, L37). */
export const askChip = (on: boolean) =>
  'display:inline-flex;align-items:center;min-height:30px;padding:0 11px;border-radius:15px;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;' +
  (on ? 'background:#22272E;color:#F6F3EC;border:1px solid #22272E;' : 'background:transparent;color:#1D3F6B;border:1px solid rgba(47,92,151,.4);');
/** A filter chip (design chip(), L46 and L227). */
export const chip = (on: boolean) =>
  'height:28px;padding:0 10px;display:inline-flex;align-items:center;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;' +
  (on ? 'background:rgba(47,92,151,.14);color:#22272E;' : 'color:#3C454F;');
/** The rail's filled and outlined buttons (design pri / sec, L47). */
export const PRI = 'height:32px;padding:0 11px;display:inline-flex;align-items:center;border-radius:7px;background:#22272E;color:#F6F3EC;font-size:14px;font-weight:700;white-space:nowrap;text-decoration:none;';
export const SEC = 'height:32px;padding:0 11px;display:inline-flex;align-items:center;border-radius:7px;border:1px solid rgba(34,39,46,.2);color:#22272E;font-size:14px;font-weight:700;white-space:nowrap;text-decoration:none;';
/** A row's button (design btnS, L197): red when urgent. */
export const rowBtn = (urgent?: boolean) =>
  'height:28px;padding:0 10px;display:inline-flex;align-items:center;border-radius:6px;font-size:14px;font-weight:700;white-space:nowrap;' +
  (urgent ? 'background:#B3261E;color:#FFFFFF;' : 'background:#22272E;color:#F6F3EC;');
/** A table signal (design sig(), L229). */
export type SigTone = 'r' | 'a' | 'g' | 'b' | 'n';
const SIG_C: Record<SigTone, [string, string]> = {
  r: ['rgba(179,38,30,.1)', '#B3261E'],
  a: ['rgba(107,74,18,.1)', '#6B4A12'],
  g: ['rgba(31,107,82,.1)', '#1F6B52'],
  b: ['rgba(47,92,151,.1)', '#2F5C97'],
  n: ['rgba(34,39,46,.07)', '#3C454F'],
};
export const sigStyle = (k: SigTone) =>
  'display:inline-block;max-width:100%;padding:2px 7px;border-radius:5px;font-size:14px;line-height:1.3;font-weight:700;vertical-align:middle;background:' + SIG_C[k][0] + ';color:' + SIG_C[k][1] + ';';
/** The rail's "why" box tint per tone (design ql.whyBg, L287). */
export const WHY_BG: Record<SigTone, string> = {
  r: 'rgba(179,38,30,.07)',
  a: 'rgba(107,74,18,.08)',
  g: 'rgba(31,107,82,.08)',
  b: 'rgba(47,92,151,.08)',
  n: 'rgba(34,39,46,.05)',
};
/** The right-hand rail (design railS, L16); `narrow` floats it over the table. */
export const railStyle = (narrow: boolean, open: boolean) =>
  'min-width:0;min-height:0;display:' + (narrow && !open ? 'none' : 'flex') +
  ';flex-direction:column;gap:10px;border:1px solid rgba(34,39,46,.14);border-radius:8px;background:#FCFAF4;padding:12px;overflow-y:auto;' +
  (narrow ? 'position:absolute;top:0;right:0;bottom:0;width:min(380px,92%);z-index:5;box-shadow:0 10px 30px rgba(34,39,46,.22);' : '');
export const CLOSE_BTN =
  'align-self:flex-end;flex:0 0 auto;white-space:nowrap;display:inline-flex;align-items:center;height:30px;padding:0 10px;border-radius:7px;border:1px solid rgba(34,39,46,.2);font-size:16px;font-weight:700;color:#22272E;text-decoration:none;';
export const KICKER = "font-family:'IBM Plex Mono',monospace;font-size:14px;letter-spacing:.1em;color:#54606C;";

// ── Money and numbers ───────────────────────────────────────────────────────
/** Dollars as capture.js money() writes them: $36.8K from ten thousand, $108K from a hundred thousand, else $6,980. */
export const money = (usd: number) =>
  Math.abs(usd) >= 10000 ? '$' + (usd / 1000).toFixed(Math.abs(usd) >= 100000 ? 0 : 1) + 'K' : '$' + Math.round(usd).toLocaleString('en-US');
/** Cents in the same form. */
export const moneyC = (cents: number) => money(cents / 100);
/** Cents as "$66.13". */
export const usd2 = (cents: number) => '$' + (cents / 100).toFixed(2);
/** Whole dollars with commas: "$29,725". */
export const usd0 = (cents: number) => '$' + Math.round(cents / 100).toLocaleString('en-US');

/** A sparkline's points (design sp(), L225). */
export function spark(a: number[], w: number, h: number): string {
  if (a.length < 2) return '';
  const mn = Math.min(...a);
  const mx = Math.max(...a);
  const r = mx - mn || 1;
  return a.map((v, i) => (i * (w / (a.length - 1))).toFixed(1) + ',' + (h - 2 - ((v - mn) / r) * (h - 4)).toFixed(1)).join(' ');
}
/** Two lines on one scale (design spJ(), L226). */
export function sparkPair(a: number[], b: number[], w: number, h: number): [string, string] {
  const all = a.concat(b);
  if (!all.length) return ['', ''];
  const mn = Math.min(...all);
  const mx = Math.max(...all);
  const r = mx - mn || 1;
  const f = (x: number[]) => (x.length < 2 ? '' : x.map((v, i) => (i * (w / (x.length - 1))).toFixed(1) + ',' + (h - 2 - ((v - mn) / r) * (h - 4)).toFixed(1)).join(' '));
  return [f(a), f(b)];
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** "Oct 2026" for "2026-10". */
export const monthLabel = (period: string) => (MONTHS[Number(period.slice(5, 7)) - 1] ?? '') + ' ' + period.slice(0, 4);
/** "Aug 2026" for "2026-08" or "2026-08-01", as the design writes a source's date. */
export const monthOf = (iso: string) => monthLabel(iso.slice(0, 7));
/** "Nov 1 2025" for "2025-11-01" (design "Signed Sep 2 2026"). */
export const dayYear = (iso: string) => dayLabel(iso) + ' ' + iso.slice(0, 4);
/** "Oct 9" for an ISO date. */
export const dayLabel = (iso: string) => {
  const d = new Date(iso.length === 10 ? iso + 'T12:00:00' : iso);
  return MONTHS[d.getMonth()] + ' ' + d.getDate();
};

/** A plain text file the browser saves (Export buttons). */
export function download(name: string, text: string, type = 'text/plain') {
  try {
    const url = URL.createObjectURL(new Blob([text], { type }));
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch {
    /* no download in this environment */
  }
}
/** One CSV line. */
export const csvLine = (cells: (string | number | null | undefined)[]) =>
  cells.map((c) => { const s = c == null ? '' : String(c); return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s; }).join(',');
